import { produce } from "immer";
import { cloneDeep, find, keyBy, sortBy } from "lodash-es";
import { Component, type ReactElement } from "react";
import Select from "react-select";

import { ActionButton, TriggerButton, TriggerButtonIntent } from "widgets";

import { Dialog } from "../overlays/Dialogs.js";
import { type ISelectOption } from "@/app-utils/form.ts";
import { type ITableColumn, type ITableData } from "@/redux/modules/table-data.ts";
import { MAX_SORTED_COLUMNS, SORT_ORDER_ASC, SORT_ORDER_DESC } from "@/settings/settings.js";
import Radio from "@/ui-modules/core/forms/Radio.js";

export interface ISortDialogProps {
  tableData: ITableData;

  onApply: (columns: ITableColumn[]) => void;
  onCancel: () => void;
}

export interface ISortDialogState {
  sortableColumns?: ITableColumn[];
  sortedColumns?: ITableColumn[];
}

const INITIAL_STATE: ISortDialogState = {
  sortableColumns: [],
  sortedColumns: [],
};

export default class TableColumnsSortDialog extends Component<ISortDialogProps, ISortDialogState> {
  constructor(props) {
    super(props);

    this.onDelete = this.onDelete.bind(this);
    this.onAdd = this.onAdd.bind(this);
    this.getColumnLabel = this.getColumnLabel.bind(this);

    this.state = INITIAL_STATE;
  }

  getColumnLabel(col: ITableColumn): string {
    return `${col.groupName ? col.groupName + " " : ""}${col.name}`;
  }

  buildSelect(sortableColumns: ITableColumn[], myCol: ITableColumn): ReactElement {
    // build option list ordered alphabetically; sortableColumns: ordered list of all columns, myCol: current column
    const optionList: ISelectOption[] = [];

    const mapOfSorted = keyBy(this.state.sortedColumns, "field");
    for (const col of sortableColumns) {
      if (!mapOfSorted[col.field] || col.field === myCol.field) {
        // prevent duplicates
        optionList.push({ value: col.field, label: this.getColumnLabel(col) });
      }
    }
    const onChange = (option: ISelectOption) => {
      // when the Select changed to a different option
      const oldValue = myCol;
      // An entry from the sortedColumns list. Could be an empty entry or a filled one.

      // Find the selected column by name in list of all columns.
      const currCol = find(sortableColumns, (col) => col.field === option.value);
      //  A real column definition.

      const pos = oldValue.sort.priority - 1;
      // Every entry in sortedColumns has a .sort.priority, even the empty ones.
      //  Put the new value into the slot of the change.

      // Transfer the sort state from the previous selection to the new selection.
      const newCol = { ...currCol, sort: cloneDeep(oldValue.sort) };

      // Install the new selection in the same position in the sortedColumns list and apply it to the local state.
      this.setState(
        produce(this.state, (draft) => {
          draft.sortedColumns![pos] = newCol;
        }),
      );
    };

    const value = myCol.field && { value: myCol.field, label: this.getColumnLabel(myCol) };
    //
    return (
      <div style={{ flex: 1 }}>
        <Select
          className="react-select"
          classNamePrefix={"react-select"}
          name="sortableColumnsSelect"
          placeholder="Please select..."
          value={value}
          isClearable={false}
          isSearchable={true}
          options={optionList}
          onChange={onChange}
          menuPortalTarget={document.body}
        />
      </div>
    );
  }

  buildOrderElements(myCol: ITableColumn): ReactElement {
    const onDeleteHandler = (...args) => {
      this.onDelete(myCol, args);
    };

    const onDirectionChange = (_e, newDir: string) => {
      const oldColumn = myCol;

      const pos = this.state.sortedColumns.findIndex((col) => col.field === oldColumn.field);

      if (this.state.sortedColumns?.[pos].sort?.direction !== newDir) {
        this.setState(
          produce(this.state, (draft) => {
            draft.sortedColumns![pos].sort!.direction = newDir;
          }),
        );
      }
    };
    const onChangeToAscendingDirection = (e) => {
      onDirectionChange(e, SORT_ORDER_ASC);
    };
    const onChangeToDescendingDirection = (e) => {
      onDirectionChange(e, SORT_ORDER_DESC);
    };

    // Do not add delete if we have only one select which is the empty select.
    let deleteIcon: ReactElement;
    if (!(!myCol.field && this.state.sortedColumns.length === 1)) {
      deleteIcon = (
        <TriggerButton
          icon={"delete"}
          label={"Remove Filter"}
          onPress={onDeleteHandler}
          intent={TriggerButtonIntent.Danger}
        />
      );
    }

    // Build radio buttons for sort order and a 'delete' icon
    return (
      <>
        <div className={"radio radio-custom"}>
          <Radio
            id={`${myCol.field}-asc`}
            name={myCol.field}
            checked={myCol.sort.direction === SORT_ORDER_ASC}
            onChange={onChangeToAscendingDirection}
            label={"Ascending"}
          />

          <Radio
            id={`${myCol.field}-desc`}
            name={myCol.field}
            checked={myCol.sort.direction === SORT_ORDER_DESC}
            onChange={onChangeToDescendingDirection}
            label={"Descending"}
          />
        </div>

        <div>{deleteIcon}</div>
      </>
    );
  }

  onDelete(column, _event) {
    //  Create a new list of sorted columns, remove the selected one
    //  and reduce all priorities behind the selected column
    //  Finally install the new list in the state.

    //  Don't delete, if this is the only column and it is empty.
    if (!column.field && this.state.sortedColumns.length === 1) {
      return;
    }

    let found = false;
    const nextList: ITableColumn[] = [];
    for (let i = 0; i < this.state.sortedColumns.length; i++) {
      const col = this.state.sortedColumns[i];
      if (found) {
        // +1 for zero vs. 1 offset, -1 for stepping over the removed item
        nextList.push(
          produce(col, (draft) => {
            draft.sort.priority = i;
          }),
        );
      } else if (col.field === column.field) {
        found = true;
      } else {
        // +1 for zero offset in array position vs. 1 offset in priority
        nextList.push(
          produce(col, (draft) => {
            draft.sort.priority = i + 1;
          }),
        );
      }
    }
    if (nextList.length === 0) {
      nextList.push(this.buildEmptySelect(nextList, this.state.sortableColumns[0]));
    }
    this.setState({ sortedColumns: nextList });
  }

  buildEmptySelect(sortedColumns: ITableColumn[], templateColumn: ITableColumn): ITableColumn {
    const newCol: ITableColumn = cloneDeep(templateColumn);
    newCol.field = null;
    newCol.name = null;
    newCol.groupName = null;
    newCol.sort = {
      direction: SORT_ORDER_ASC,
      priority: sortedColumns.length + 1, // priority is one based
    };
    return newCol;
  }

  onAdd() {
    const { sortedColumns } = this.state;
    const nextList = cloneDeep(sortedColumns);
    nextList.push(this.buildEmptySelect(sortedColumns, this.state.sortableColumns[0]));
    this.setState({ ...this.state, sortedColumns: nextList });
  }

  buildColumnList(_cols: ITableColumn[]): ReactElement[] {
    const { sortedColumns, sortableColumns } = this.state;
    const myList: ReactElement[] = [];
    for (let myIdx = 0; myIdx < sortedColumns.length; myIdx++) {
      const myLabel: string = myIdx === 0 ? "Sort by" : "Then by";
      const myCol: ITableColumn = sortedColumns[myIdx];
      const selectElement: ReactElement = this.buildSelect(sortableColumns, myCol);
      const orderElements: ReactElement = this.buildOrderElements(myCol);

      myList.push(
        // was: myCol.field
        <div key={myIdx + 1} className="form-group">
          <label style={{ margin: 0 }}>{myLabel}</label>
          <div style={{ display: "flex", columnGap: 20, alignItems: "center" }}>
            {selectElement}
            {orderElements}
          </div>
        </div>,
      );
    }
    return myList;
  }

  initState(cols: ITableColumn[]): void {
    // sortedColumns: extract the sorted cols and sort them by priority
    let sortedColumns: ITableColumn[] = cols
      .filter((col) => !!col.sort)
      .sort((col1, col2) => col1.sort.priority - col2.sort.priority);
    // isolate the state from the data of the caller.
    //  We have to manipulate the sort priority and that change must not seep through to the parent.
    sortedColumns = cloneDeep(sortedColumns);

    // sortableColumns: all columns sorted by groupName/name alphabetically
    const sortableColumns: ITableColumn[] = sortBy(
      cols.filter((col: ITableColumn) => col.sortable),
      this.getColumnLabel,
    );

    if (sortedColumns.length === 0 && sortableColumns.length > 0) {
      sortedColumns.push(this.buildEmptySelect(sortedColumns, sortableColumns[0]));
    }

    this.setState({ sortedColumns: sortedColumns, sortableColumns: sortableColumns });
  }

  componentDidMount() {
    this.initState(this.props.tableData.columns);
  }

  render() {
    const onCancelHandler = () => {
      this.props.onCancel();
    };
    const onApplyHandler = () => {
      this.props.onApply(
        this.state.sortedColumns
          .filter((col) => !!col.field)
          .map((col, index) => {
            return produce(col, (draft) => {
              draft.sort.priority = index + 1;
            });
          }),
      );
    };

    const disabledLink: boolean =
      this.state.sortedColumns.length === this.state.sortableColumns.length ||
      this.state.sortedColumns.length >= MAX_SORTED_COLUMNS;
    // Limit the number of sorted columns:
    //  - at most one for each sortable column.
    //  - less than 10 sorted columns.

    return (
      <Dialog
        headerProps={{ title: "Advanced Sort" }}
        mainActionButtonProps={{
          children: "Apply",
          onPress: onApplyHandler,
        }}
        closeFn={onCancelHandler}
      >
        {this.buildColumnList(this.props.tableData.columns)}
        <div style={{ marginTop: 40, textAlign: "center" }}>
          <ActionButton onPress={this.onAdd} isDisabled={disabledLink} icon={"add"} label={"Add Criteria"} />
        </div>
      </Dialog>
    );
  }
}
